import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useLazyQuery, useQuery } from '@apollo/client/react';
import { Loader2, Eye, EyeOff, Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Label,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from '@scio3/ui';
import {
  ADD_NEW_USER_TEAM_BILLING,
  STRIPE_BUILD_ELEMENT,
  PLAN_FEATURES_QUERY,
} from '@scio3/graphql-client';
import type { StripeBuildElementVariables } from '@scio3/graphql-client';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';
import { isEnterKey } from '@/lib/keyboard';
import { DialogScrollBody } from '@/components/shared';
import { useBillingData } from '../hooks/useBillingData';
import { useRecaptchaMutation } from '@/features/auth/hooks/useRecaptchaMutation';
import { RecaptchaV2Challenge } from '@/features/auth/components/RecaptchaV2Challenge';
import { useRecaptchaDialogFix } from '@/features/auth/hooks/useRecaptchaDialogFix';
import { StripeCardForm } from '../components/StripeCardForm';
import { COUNTRIES } from '@/features/settings/utils/profile-constants';
import { getPlanDisplayName } from '../utils/plan-helpers';
import { BILLING_INTERVAL } from '@scio3/shared-types';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { generateComplexPassword } from '@/lib/secure-password';

type PaymentMethodType = 'card' | 'invoice';

interface CreatedTeamBillingResult {
  email: string;
  username: string;
  password: string;
  teamId?: string;
}

/**
 * Dialog to enable team billing — creates a new team with its own Stripe
 * subscription, OR promotes an existing team (when `teamId` is supplied via
 * dialog context). Mirrors the UI pattern of CreateSubAccountDialog.
 *
 * Opens via `billing-dialog-store` with `activeDialog === 'enableTeamBilling'`.
 * Optional `dialogContext.teamId` switches the form to promote mode.
 */
export function EnableTeamBillingDialog() {
  const { t } = useTranslation();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);

  const open = activeDialog === 'enableTeamBilling';
  const promoteTeamId = (dialogContext?.teamId as string | undefined) ?? undefined;
  const promoteTeamName = (dialogContext?.teamName as string | undefined) ?? '';
  const isPromote = !!promoteTeamId;

  const { currency, planFeature, payment, currencySymbol } = useBillingData();
  const { run: runWithCaptcha, challengeRequired, renderV2Widget } = useRecaptchaMutation(open);
  useRecaptchaDialogFix(open && challengeRequired);

  // Form state
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminEmailManuallyEdited, setAdminEmailManuallyEdited] = useState(false);
  const [autoGeneratePassword, setAutoGeneratePassword] = useState(true);
  const [password, setPassword] = useState(() => generateComplexPassword());
  const [showPassword, setShowPassword] = useState(false);
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [numberOfScreens, setNumberOfScreens] = useState(1);
  const [sendCredentials, setSendCredentials] = useState(false);
  const [sendCredentialsEmail, setSendCredentialsEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('card');
  const [country, setCountry] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [memo, setMemo] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const billingInterval =
    ((payment as Record<string, unknown> | undefined)?.plan as string | undefined) ??
    BILLING_INTERVAL.MONTHLY;

  // Plan features — pricing display + planFeatureId for mutation payload
  const { data: planFeaturesData } = useQuery(PLAN_FEATURES_QUERY, {
    variables: { query: { alias: planFeature } },
    skip: !planFeature || planFeature === 'free',
  });

  // Success state
  const [createdResult, setCreatedResult] = useState<CreatedTeamBillingResult | null>(null);
  const [showCreatedPassword, setShowCreatedPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Stripe setup for card payment
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripePaymentMethodOrder, setStripePaymentMethodOrder] = useState<string[]>([]);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);

  const [fetchStripeBuildElement, { loading: stripeLoading }] = useLazyQuery(STRIPE_BUILD_ELEMENT, {
    fetchPolicy: 'network-only',
  });

  const [addNewUserTeamBilling] = useMutation(ADD_NEW_USER_TEAM_BILLING, {
    refetchQueries: ['getAllTeams', 'GetTeamBillingInfo'],
  });

  const isRequiredFilled =
    (isPromote || !!teamName.trim()) &&
    !!billingEmail.trim() &&
    !!adminEmail.trim() &&
    numberOfScreens > 0 &&
    (!autoGeneratePassword ? !!password.trim() : true) &&
    (paymentMethod === 'invoice' ? !!country : true);

  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: isSubmitting || stripeLoading,
    isRequiredFilled,
  });

  const fetchFreshSetupIntent = useCallback(async () => {
    try {
      // Create mode (no promoteTeamId): request an UNATTACHED SetupIntent so
      // the paymentMethod isn't bound to the parent's customer. Backend
      // creates a dedicated Stripe customer for the new team and attaches the
      // PM to it in registerPaymentMethod.
      //
      // Promote mode: attach SetupIntent to the existing team's customer so
      // the card ends up on that team.
      const variables: StripeBuildElementVariables = {
        form: 'TEAM',
        currency,
        ...(promoteTeamId ? { teamId: promoteTeamId } : { isAttachCustomer: false }),
      };
      const { data } = await fetchStripeBuildElement({ variables });
      const result = (data as Record<string, unknown> | undefined)?.stripeBuildElement as
        | Record<string, unknown>
        | undefined;
      if (result?.client_secret) {
        setStripeClientSecret(result.client_secret as string);
        setStripePaymentMethodOrder((result.paymentMethodOrder as string[] | undefined) ?? []);
      }
    } catch {
      setErrorMessage(t('Failed to initialize payment form'));
    }
  }, [fetchStripeBuildElement, currency, promoteTeamId, t]);

  const handlePaymentMethodChange = useCallback(
    (method: PaymentMethodType) => {
      setPaymentMethod(method);
      setPaymentMethodId(null);
      if (method === 'card') {
        setStripeClientSecret(null);
        fetchFreshSetupIntent();
      }
    },
    [fetchFreshSetupIntent],
  );

  // Auto-submit after Stripe card confirmation
  useEffect(() => {
    if (pendingSubmit && paymentMethodId) {
      setPendingSubmit(false);
      setErrorMessage('');
      handleSubmit();
    }
  }, [pendingSubmit, paymentMethodId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize Stripe when dialog opens with card mode
  useEffect(() => {
    if (open && paymentMethod === 'card') {
      fetchFreshSetupIntent();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStripeSuccess = useCallback((pmId: string) => {
    setPaymentMethodId(pmId);
    setPendingSubmit(true);
    setStripeClientSecret(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    setErrorMessage('');
    // The backend requires CAPTCHA before any team, Stripe, or email work.

    setIsSubmitting(true);

    try {
      const planNode = (planFeaturesData as Record<string, unknown> | undefined)?.planFeatures as
        | Record<string, unknown>
        | undefined;
      const planEdges = (planNode?.page as Record<string, unknown>)?.edges as
        | { node: Record<string, unknown> }[]
        | undefined;
      const planFeatureId = (planEdges?.[0]?.node?._id as string) ?? '';

      const input: Record<string, unknown> = {
        // Team
        ...(isPromote
          ? { teamId: promoteTeamId }
          : {
              name: teamName.trim(),
              description: teamDescription.trim() || undefined,
            }),
        numberOfScreens,
        // Owner user
        email: adminEmail.trim(),
        username: adminEmail.trim(),
        password,
        autoGeneratePassword,
        requirePasswordChange,
        // Billing
        billingEmail: billingEmail.trim(),
        currency,
        planAlias: billingInterval,
        planFeatureId,
        // Payment
        automaticallyCharge: paymentMethod === 'card' ? 1 : 2,
        memo: memo.trim() || undefined,
        ...(sendCredentials && sendCredentialsEmail.trim()
          ? { notifyEmail: sendCredentialsEmail.trim() }
          : {}),
      };

      if (paymentMethod === 'card') {
        if (!paymentMethodId) {
          // Stripe confirmSetup is async — keep isSubmitting=true so the button
          // stays in "processing" state. handleStripeSuccess (→ auto-submit via
          // useEffect) or StripeCardForm onError will clear it.
          const stripeForm = document.getElementById('stripe-payment-form') as HTMLFormElement;
          if (stripeForm) {
            stripeForm.requestSubmit();
            setErrorMessage('');
          } else {
            setErrorMessage(t('Please complete the payment form'));
            setIsSubmitting(false);
          }
          return;
        }
        input.paymentMethodId = paymentMethodId;
      } else {
        if (country) input.country = country;
        if (zipCode) input.postal_code = zipCode;
      }

      const { data } = await runWithCaptcha('enable_team_billing', (captcha) =>
        addNewUserTeamBilling({
          variables: {
            input: {
              ...input,
              token: captcha.captcha,
              captchaVersion: captcha.captchaVersion,
              captchaAction: captcha.captchaAction,
            },
          },
        }),
      );
      const result = (data as Record<string, unknown> | undefined)?.addNewUserTeamBilling as
        | Record<string, unknown>
        | undefined;

      if (result?.status === true) {
        const generatedPw = (result.generatedPassword as string | undefined) ?? password;
        setCreatedResult({
          email: adminEmail.trim(),
          username: adminEmail.trim(),
          password: generatedPw,
          teamId: result.teamId as string | undefined,
        });
      } else {
        // Always prefer the server message when present — pinpoints failing step.
        const code = result?.code as string | undefined;
        const message = result?.message as string | undefined;
        const msg =
          message ||
          (code === 'DUPLICATE_EMAIL'
            ? t('Email is already in use')
            : code === 'ALREADY_BILLING'
              ? t('Team already has team billing enabled')
              : code === 'ZIPCODE_ISSUE'
                ? t('ZIP / postal code is required for US invoice billing')
                : code === 'PLAN_DISALLOWED'
                  ? t('Your plan does not allow team billing')
                  : code === 'STRIPE_FAILED'
                    ? t('Payment failed. Please try again.')
                    : code === 'ROLLBACK'
                      ? t('Creation failed and was rolled back. Please try again.')
                      : t('Failed to create team billing'));
        setErrorMessage(code ? `[${code}] ${msg}` : msg);
        if (paymentMethod === 'card') {
          setPaymentMethodId(null);
          setStripeClientSecret(null);
          fetchFreshSetupIntent();
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Failed to create team billing');
      setErrorMessage(message);
      if (paymentMethod === 'card') {
        setPaymentMethodId(null);
        setStripeClientSecret(null);
        fetchFreshSetupIntent();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isPromote,
    promoteTeamId,
    teamName,
    teamDescription,
    billingEmail,
    adminEmail,
    password,
    autoGeneratePassword,
    requirePasswordChange,
    numberOfScreens,
    sendCredentials,
    sendCredentialsEmail,
    paymentMethod,
    paymentMethodId,
    country,
    zipCode,
    memo,
    currency,
    planFeaturesData,
    billingInterval,
    addNewUserTeamBilling,
    t,
    runWithCaptcha,
    fetchFreshSetupIntent,
  ]);

  const handleCopy = useCallback(async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // no-op
    }
  }, []);

  const resetForm = useCallback(() => {
    setTeamName('');
    setTeamDescription('');
    setBillingEmail('');
    setAdminEmail('');
    setAdminEmailManuallyEdited(false);
    setAutoGeneratePassword(true);
    setPassword(generateComplexPassword());
    setShowPassword(false);
    setRequirePasswordChange(false);
    setNumberOfScreens(1);
    setSendCredentials(false);
    setSendCredentialsEmail('');
    setPaymentMethod('card');
    setCountry('');
    setZipCode('');
    setMemo('');
    setErrorMessage('');
    setCreatedResult(null);
    setShowCreatedPassword(false);
    setPaymentMethodId(null);
    setStripeClientSecret(null);
  }, []);

  const handleOpenChange = (v: boolean) => {
    if (!v) {
      resetForm();
      closeDialog();
    }
  };

  const isUS = country === 'US';

  const { planDisplayName, discountText } = useMemo(() => {
    const planNode = (planFeaturesData as Record<string, unknown> | undefined)?.planFeatures as
      | Record<string, unknown>
      | undefined;
    const edges = (planNode?.page as Record<string, unknown>)?.edges as
      | { node: Record<string, unknown> }[]
      | undefined;
    const node = edges?.[0]?.node;

    if (!node) {
      return {
        planDisplayName: getPlanDisplayName(planFeature),
        discountText: '',
      };
    }

    const plans = node.plans as Record<string, Record<string, Record<string, unknown>>> | undefined;
    const intervalPlan = plans?.[currency]?.[billingInterval];
    const planName = (node.name as string) ?? getPlanDisplayName(planFeature);
    const intervalName = (intervalPlan?.name as string) ?? billingInterval;
    const price = intervalPlan?.price as number | undefined;
    const discount = (intervalPlan?.discount_text as string) ?? '';

    const display =
      price != null
        ? `${planName} - ${intervalName} - ${currencySymbol}${price}`
        : `${planName} - ${intervalName}`;

    return { planDisplayName: display, discountText: discount };
  }, [planFeaturesData, planFeature, currency, billingInterval, currencySymbol]);

  // =========================================
  // Success State
  // =========================================
  if (createdResult) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          data-testid="enable-team-billing-success-dialog"
          className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>{t('Team billing created')}</DialogTitle>
          </DialogHeader>

          <DialogScrollBody className="space-y-4">
            <p className="text-sm">
              {t('Team owner account created for')} <strong>{createdResult.email}</strong>
            </p>

            {/* Username */}
            <div className="space-y-2">
              <Label>{t('Email/Username')}</Label>
              <div className="flex items-center gap-2">
                <Input value={createdResult.username} readOnly />
                <Button
                  variant="ghost"
                  size="icon-md"
                  aria-label={t('Copy username')}
                  className="shrink-0 cursor-pointer"
                  onClick={() => handleCopy(createdResult.username, 'username')}
                >
                  {copiedField === 'username' ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label>{t('Password')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type={showCreatedPassword ? 'text' : 'password'}
                  value={createdResult.password}
                  readOnly
                />
                <Button
                  variant="ghost"
                  size="icon-md"
                  aria-label={t('Toggle password visibility')}
                  className="shrink-0 cursor-pointer"
                  onClick={() => setShowCreatedPassword((v) => !v)}
                >
                  {showCreatedPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon-md"
                  aria-label={t('Copy password')}
                  className="shrink-0 cursor-pointer"
                  onClick={() => handleCopy(createdResult.password, 'password')}
                >
                  {copiedField === 'password' ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </DialogScrollBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" className="cursor-pointer">
                {t('Close')}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // =========================================
  // Form State
  // =========================================
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="enable-team-billing-dialog"
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>
            {isPromote ? t('Enable Team Billing on Existing Team') : t('Create New Team Billing')}
          </DialogTitle>
        </DialogHeader>

        <DialogScrollBody className="space-y-4">
          {/* Team section */}
          {isPromote ? (
            <div className="space-y-2">
              <Label>{t('Team')}</Label>
              <Input value={promoteTeamName} readOnly className="opacity-70" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="tb-team-name">{t('Team name')} *</Label>
                <Input
                  id="tb-team-name"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder={t('Enter team name')}
                  maxLength={64}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tb-team-description">{t('Description (optional)')}</Label>
                <Textarea
                  id="tb-team-description"
                  value={teamDescription}
                  onChange={(e) => setTeamDescription(e.target.value)}
                  rows={2}
                  maxLength={256}
                />
              </div>
            </>
          )}

          {/* Billing Email */}
          <div className="space-y-2">
            <Label htmlFor="tb-billing-email">{t('Billing email')} *</Label>
            <Input
              id="tb-billing-email"
              type="email"
              value={billingEmail}
              onChange={(e) => {
                const val = e.target.value;
                setBillingEmail(val);
                if (!adminEmail || !adminEmailManuallyEdited) {
                  setAdminEmail(val);
                }
              }}
              placeholder={t('Enter billing email')}
            />
          </div>

          {/* Admin Email */}
          <div className="space-y-2">
            <Label htmlFor="tb-admin-email">{t('Team owner email')} *</Label>
            <Input
              id="tb-admin-email"
              type="email"
              value={adminEmail}
              onChange={(e) => {
                setAdminEmail(e.target.value);
                setAdminEmailManuallyEdited(true);
              }}
              placeholder={t('Enter team owner email')}
            />
          </div>

          {/* Password section */}
          <div className="space-y-3">
            <Label className="font-semibold">{t('Team owner password')}</Label>

            <div className="flex items-center gap-3">
              <Switch
                id="tb-auto-password"
                checked={autoGeneratePassword}
                onCheckedChange={(checked) => {
                  setAutoGeneratePassword(checked);
                  if (checked) setPassword(generateComplexPassword());
                }}
                className="cursor-pointer"
              />
              <Label htmlFor="tb-auto-password" className="cursor-pointer font-normal">
                {t('Automatically generate a password')}
              </Label>
            </div>

            {!autoGeneratePassword && (
              <div className="flex items-center gap-2">
                <Input
                  id="tb-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('Enter password')}
                />
                <Button
                  variant="ghost"
                  size="icon-md"
                  aria-label={t('Toggle password visibility')}
                  className="shrink-0 cursor-pointer"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch
                id="tb-require-password-change"
                checked={requirePasswordChange}
                onCheckedChange={setRequirePasswordChange}
                className="cursor-pointer"
              />
              <Label htmlFor="tb-require-password-change" className="cursor-pointer font-normal">
                {t('Ask for a password change at the next sign-in')}
              </Label>
            </div>
          </div>

          {/* Number of screens */}
          <div className="space-y-2">
            <Label htmlFor="tb-num-screens">{t('Number of screens')} *</Label>
            <Input
              id="tb-num-screens"
              type="number"
              min={1}
              value={numberOfScreens}
              onChange={(e) => setNumberOfScreens(Math.max(1, parseInt(e.target.value) || 1))}
            />
          </div>

          {/* Send credentials */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Switch
                id="tb-send-credentials"
                checked={sendCredentials}
                onCheckedChange={setSendCredentials}
                className="cursor-pointer"
              />
              <Label htmlFor="tb-send-credentials" className="cursor-pointer font-normal">
                {t('Send credentials to')}
              </Label>
            </div>
            {sendCredentials && (
              <Input
                type="email"
                value={sendCredentialsEmail}
                onChange={(e) => setSendCredentialsEmail(e.target.value)}
                placeholder={t('Enter email address')}
                aria-label={t('Credentials recipient email')}
              />
            )}
          </div>

          {/* Currency — inherited from parent */}
          <div className="space-y-2">
            <Label>{t('Currency')} *</Label>
            <Select disabled value={currency}>
              <SelectTrigger className="opacity-70">
                <SelectValue placeholder={`${currency.toUpperCase()} ${currencySymbol}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={currency}>
                  {currency.toUpperCase()} {currencySymbol}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Plan — inherited from parent */}
          <div className="space-y-2">
            <Label>{t('Plan')} *</Label>
            <Select disabled value={planFeature}>
              <SelectTrigger className="opacity-70">
                <SelectValue placeholder={planDisplayName} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={planFeature}>{planDisplayName}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {discountText && (
            <p className="text-sm font-semibold">
              {t('Discount')} {discountText}
            </p>
          )}

          {/* Payment method */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Switch
                id="tb-payment-card"
                checked={paymentMethod === 'card'}
                onCheckedChange={(checked) =>
                  handlePaymentMethodChange(checked ? 'card' : 'invoice')
                }
                className="cursor-pointer"
              />
              <Label htmlFor="tb-payment-card" className="cursor-pointer font-normal">
                {t('Automatically charge this payment method on file')}
              </Label>
            </div>

            {paymentMethod === 'card' && stripeClientSecret && (
              <StripeCardForm
                key={stripeClientSecret}
                clientSecret={stripeClientSecret}
                paymentMethodOrder={stripePaymentMethodOrder}
                hideEmail
                showSubmitButton={false}
                onSuccess={handleStripeSuccess}
                onError={(msg) => {
                  setErrorMessage(msg);
                  setIsSubmitting(false);
                }}
              />
            )}

            {paymentMethod === 'card' && !stripeClientSecret && stripeLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {billingInterval !== BILLING_INTERVAL.YEARLY_FREEDEVICE && (
              <div className="flex items-center gap-3">
                <Switch
                  id="tb-payment-invoice"
                  checked={paymentMethod === 'invoice'}
                  onCheckedChange={(checked) =>
                    handlePaymentMethodChange(checked ? 'invoice' : 'card')
                  }
                  className="cursor-pointer"
                />
                <Label htmlFor="tb-payment-invoice" className="cursor-pointer font-normal">
                  {t('Email invoice to')} {billingEmail || t('billing email')}
                </Label>
              </div>
            )}

            {paymentMethod === 'invoice' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="tb-country">{t('Country')}</Label>
                  <Select value={country || undefined} onValueChange={setCountry}>
                    <SelectTrigger id="tb-country" className="cursor-pointer">
                      <SelectValue placeholder={t('Select country')} />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.code} value={c.code} className="cursor-pointer">
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {isUS && (
                  <div className="space-y-2">
                    <Label htmlFor="tb-zip">{t('Zip / Postal Code')}</Label>
                    <Input
                      id="tb-zip"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Memo */}
          <div className="space-y-2">
            <Label htmlFor="tb-memo">{t('Memo')}</Label>
            <Textarea
              id="tb-memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder={t('Reference PO, or payment instruction, etc.')}
              rows={4}
            />
          </div>

          {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
        </DialogScrollBody>

        <RecaptchaV2Challenge visible={challengeRequired} renderV2Widget={renderV2Widget} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" className="cursor-pointer">
              {t('Cancel')}
            </Button>
          </DialogClose>
          <Button
            onClick={handleSubmit}
            disabled={submitDisabled}
            title={disabledTooltip}
            className={cn('cursor-pointer', submitDisabled && 'opacity-50 cursor-not-allowed')}
            onKeyDown={(e) => {
              if (isEnterKey(e) && !submitDisabled) {
                handleSubmit();
              }
            }}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPromote ? t('Enable Team Billing') : t('Create Team Billing')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
